import React, { useState } from 'react';

import { faCircleInfo, faClose } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { css, styled } from 'styled-components';

import ContentContainer from 'components/ContentContainer/ContentContainer';
import CtaButton from 'components/CtaButton/CtaButton';
import InnerHtml, { UnstyledInnerHtml } from 'components/InnerHtml/InnerHtml';
import { getFullBleedCtaMedia, getRadialPosition } from 'components/ModularBlocks/FullBleedCta/fullBleedCta.utils';
import { MediaQuery } from 'components/StyledComponents/MediaQueryRendering';

import { FullBleedCtaStandard } from 'interfaces/content-stack/modular-blocks/fullBleedCta';
import {
  ContentAlignmentType,
  ContentWidthPercentageValues,
  ContentWidthPercentages,
  JustifyContentType,
} from 'interfaces/styles';

import { autoOptimizeImage } from 'utils/functions';
import { getModularBlockLink } from 'utils/modularBlockUtils';

export const FullBleedStandardBlockCtaContainer = styled.div<{ $height?: number }>`
  background-color: ${(props) => props.theme.colors.black};
  display: flex;
  align-items: center;
  background-image: none;
  background-repeat: repeat;
  background-size: auto;
  background-attachment: scroll;
  flex-direction: column;
  justify-content: center;
  color: ${(props) => props.theme.colors.white};
  position: relative;
  height: ${({ $height }) => ($height ? `${$height}px` : `725px`)};
`;

const CtaContainerContainer = styled.div<{
  $justifyContent?: ContentAlignmentType;
}>`
  width: 100%;
  display: flex;

  justify-content: ${(props) => props.$justifyContent ?? 'space-around'};
  text-align: ${(props) => props.$justifyContent ?? 'left'};
`;

export const CtaContainer = styled.div<{
  $width?: ContentWidthPercentageValues;
}>`
  display: flex;
  flex-direction: column;

  ${(props) => `
    width: ${props.$width ?? '50%'};
    justify-content: space-around;

    @media(max-width: ${props.theme.breakpoints.lg}) {
      width: 100%;
    }
  `}

  img {
    width: 60%;
    align-self: center;
    justify-self: center;

    ${({ theme }) => `
       @media (max-width: ${theme.breakpoints.lg}) {
         margin-top: 3rem;
       }

       @media (max-width: ${theme.breakpoints.xs}) {
         width: 100%;
       }
    `}
  }
`;

export const CtaTextWrapper = styled.div<{ $justifyContent: JustifyContentType }>`
  display: flex;
  max-width: 65rem;
  flex-direction: column;
  position: relative;
  align-items: ${(props) => props.$justifyContent};

  @media (max-width: 1920px) {
    max-width: 54rem;
  }

  @media (max-width: 1440px) {
    max-width: 47rem;
  }

  @media (max-width: 991px) {
    max-width: none;
  }
`;

const ModalHeader = styled.h2`
  font-family: ${(props) => props.theme.fonts.fontFamilySecondary};
  text-transform: uppercase;
`;

export const HeadingWrapper = styled.div<{ $justifyContent: JustifyContentType }>`
  flex-direction: column;
  margin-bottom: 2.5rem;
  display: flex;
  gap: 1rem;
  align-items: ${(props) => props.$justifyContent};
`;

export const CtaTitle = styled.h2`
  font-family: ${(props) => props.theme.fonts.fontFamilySecondary};
  font-size: 5.5rem;
  text-transform: uppercase;
  font-weight: 500;
  line-height: 1.6;

  font-size: 3rem;

  & sup {
    font-size: 0.4em;
    top: -1.75em;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 2.75rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.xs}) {
    font-size: 2rem;
  }
`;

export const CtaText = styled.div`
  font-weight: 500;
  font-family: ${(props) => props.theme.fonts.default};
  font-size: 1.25rem;

  a {
    color: inherit;

    &:hover {
      color: ${(props) => props.theme.colors.primaryRed};
    }
  }

  @media (min-width: 1920px) {
    font-size: 1.5rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.xs}) {
    font-size: 1rem;
  }
`;

export const BackgroundMediaContainer = styled.div<{ $fixedImage?: string }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  video,
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    background-position: 50%;
    background-size: cover;
    margin: auto;
    position: absolute;
    top: -100%;
    bottom: -100%;
    left: -100%;
    right: -100%;
  }

  ${(props) =>
    props.$fixedImage &&
    `
    background-image: url(${props.$fixedImage});
    background-position: 50%;
    background-repeat: no-repeat;
    background-size: cover;
    background-attachment: fixed;
    `}
`;

export const CtaOverlay = styled.div<{ $radialPosition?: string }>`
  mix-blend-mode: multiply;
  background-image: radial-gradient(
    circle ${(props) => props.$radialPosition && `at ${props.$radialPosition}`},
    rgba(29, 25, 27, 0.95) 29%,
    rgba(27, 27, 27, 0) 94%
  );
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;

const CtaComponentT3 = styled.div<{ $bgImage?: string; $tabletHeight?: number; $mobileHeight?: number }>`
  ${(props) => css`
    ${props.$tabletHeight && `height: ${props.$tabletHeight}px;`}
    ${props.$mobileHeight &&
    `
      @media (max-width: ${props.theme.breakpoints.sm}) {
        height: ${props.$mobileHeight}px;
      }
    `}
  `}
  background-color: ${(props) => props.theme.colors.black};
  color: ${(props) => props.theme.colors.white};
  background-image: url(${(props) => props.$bgImage});
  background-position: 0 0;
  background-repeat: no-repeat;
  background-size: auto;
  background-attachment: scroll;
  flex-direction: column;
  align-items: center;
  display: flex;
  overflow: hidden;
  position: relative;
`;

const CtaCompT3Img = styled.img`
  width: 100%;
  height: 100%;
  aspect-ratio: 2 / 1;
  object-fit: cover;
  object-position: 50% 0%;
  display: block;
  position: relative;
`;

const CtaCompT3HeadingWrapper = styled.div`
  grid-row-gap: 0.75rem;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 2.5rem;
  display: flex;
`;

const CtaCompT3Overlay = styled.div<{ $radialPosition?: string }>`
  backdrop-filter: blur(30px);
  background-color: rgba(0, 0, 0, 0.3);
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;

const CtaButtonsContainer = styled.div`
  display: flex;
  grid-column-gap: 1.5rem;
  align-items: center;

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    flex-direction: column;
    grid-row-gap: 1.5rem;
  }
`;

const ModalTrigger = styled.a`
  cursor: pointer;
  font-size: 1rem;
  grid-column-gap: 0.75rem;
  color: ${(props) => props.theme.colors.white};
  align-items: center;
  font-weight: 500;
  text-decoration: none;
  display: flex;

  &:hover {
    color: ${(props) => props.theme.colors.white} !important;
    border-bottom: 1px solid ${(props) => props.theme.colors.ttRed};
  }
`;

const ModalContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${(props) => props.theme.colors.darkTransparentBlack};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const ModalBody = styled.div`
  font-weight: 500;

  a {
    color: ${(props) => props.theme.colors.black};
  }
`;

const ModalContent = styled.div<{ $closeAnimation: boolean }>`
  background-color: ${(props) => props.theme.colors.white};
  width: 40rem;
  grid-column-gap: 1.5rem;
  grid-row-gap: 1.5rem;
  color: ${(props) => props.theme.colors.primaryBlack};
  flex-direction: column;
  padding: 5rem;
  display: flex;
  position: relative;
  animation: ${(props) => (props.$closeAnimation ? 'slideDown' : 'slideUp')} 0.5s ease-in-out forwards;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(100%);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideDown {
    from {
      opacity: 1;
      transform: translateY(0);
    }
    to {
      opacity: 0;
      transform: translateY(100%);
    }
  }
`;

const ModalClose = styled.span`
  position: absolute;
  top: 1rem;
  right: 1rem;
  cursor: pointer;
  background-color: ${(props) => props.theme.colors.ttRed};
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  color: ${(props) => props.theme.colors.white};
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
`;

const DisclaimerContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledIcon = styled(FontAwesomeIcon)`
  font-size: 1rem;
`;

const CtaDisclaimerContainer = styled.div`
  margin-top: 3.5rem;
  font-size: 0.75rem;
  font-weight: 500;

  a {
    color: inherit;

    &:hover {
      color: ${(props) => props.theme.colors.primaryRed};
    }
  }

  & span {
    color: ${(props) => props.theme.colors.white};

    p {
      margin-bottom: 0;
    }
  }
`;

const StandardFullbleedCta = (cta: FullBleedCtaStandard) => {
  const [closeAnimate, setCloseAnimate] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const openModal = () => {
    setShowModal(true);
  };

  const closeModal = () => {
    setCloseAnimate(true);
    setTimeout(() => {
      setShowModal(false);
      setCloseAnimate(false);
    }, 400);
  };

  const {
    buttonCta,
    backgroundImageOrVideo,
    ctaHeader,
    ctaDescription,
    contentAlignment = 'left',
    contentWidthSpan = '1/2',
    disclaimer,
    disclaimerModal,
    ctaImageOverlay,
    sizing,
  } = cta;
  const media = getFullBleedCtaMedia(backgroundImageOrVideo);
  const radialPosition = getRadialPosition(contentAlignment);
  const justifyContent = contentAlignment === 'left' ? 'start' : contentAlignment === 'right' ? 'end' : 'center';
  const headerText = ctaHeader.replaceAll(/<\/? *p[^>]*>/g, '');

  const CtaButtons = buttonCta && (
    <CtaButtonsContainer>
      {buttonCta?.map((button, index) => (
        <CtaButton
          key={index}
          text={button.buttonText}
          to={getModularBlockLink(button.link)}
          buttonType={button.buttonType}
          textColor='white'
          openInNewWindow={button.link?.[0]?.openInNewWindow}
          banner
          dataLayerInfo={headerText}
        />
      ))}
    </CtaButtonsContainer>
  );

  const modalTrigger = disclaimerModal && disclaimerModal.modalTrigger && (
    <ModalTrigger className='modal-trigger-cta-disclaimer' onClick={openModal}>
      <StyledIcon icon={faCircleInfo} />
      {disclaimerModal.modalTrigger}
    </ModalTrigger>
  );

  const Disclaimer = disclaimer && (
    <CtaDisclaimerContainer>
      <UnstyledInnerHtml richText={disclaimer} />
      <DisclaimerContainer>{modalTrigger}</DisclaimerContainer>
    </CtaDisclaimerContainer>
  );

  return (
    <>
      {showModal && (
        <ModalContainer>
          <ModalContent $closeAnimation={closeAnimate}>
            <ModalClose onClick={closeModal}>
              <FontAwesomeIcon icon={faClose} />
            </ModalClose>
            <ModalHeader>{disclaimerModal?.modalContent?.modalHeader}</ModalHeader>
            {disclaimerModal?.modalContent?.modalBody && (
              <ModalBody>
                <UnstyledInnerHtml richText={disclaimerModal.modalContent.modalBody} />
              </ModalBody>
            )}
          </ModalContent>
        </ModalContainer>
      )}
      <MediaQuery $mobile>
        <CtaComponentT3
          $bgImage={media.showMobileImageAsBackground ? autoOptimizeImage(media.mobile) : undefined}
          $mobileHeight={sizing?.mobileHeight}
          $tabletHeight={sizing?.tabletHeight}
        >
          {!media.showMobileImageAsBackground && (
            <CtaCompT3Img
              src={autoOptimizeImage(media.mobile)}
              alt={backgroundImageOrVideo.altImageText}
              loading='lazy'
            />
          )}
          <ContentContainer>
            <CtaCompT3HeadingWrapper>
              <CtaTitle>
                <UnstyledInnerHtml richText={headerText} />
              </CtaTitle>
              <CtaText>
                <InnerHtml richText={ctaDescription} />
              </CtaText>
            </CtaCompT3HeadingWrapper>
            {CtaButtons}
            {Disclaimer}
            {ctaImageOverlay && (
              <CtaContainer>
                <img
                  src={autoOptimizeImage(ctaImageOverlay.image?.url)}
                  alt={ctaImageOverlay.altImageText ?? ctaHeader + ': img'}
                  loading='lazy'
                />
              </CtaContainer>
            )}
          </ContentContainer>
          {backgroundImageOrVideo.blurBackground && <CtaCompT3Overlay />}
        </CtaComponentT3>
      </MediaQuery>
      <MediaQuery $desktop>
        <FullBleedStandardBlockCtaContainer $height={sizing?.desktopHeight}>
          <BackgroundMediaContainer
            $fixedImage={autoOptimizeImage(media.fixedDesktopImage) ?? media.fixedDesktopImage ?? ''}
          >
            {!media.fixedDesktopImage && (
              <>
                {media.type === 'image' ? (
                  <img
                    src={autoOptimizeImage(media.desktop)}
                    alt={backgroundImageOrVideo.altImageText ?? ctaHeader + ': img'}
                    loading='lazy'
                  />
                ) : (
                  <video autoPlay loop muted>
                    <source src={media.desktop} />
                  </video>
                )}
              </>
            )}
          </BackgroundMediaContainer>
          <ContentContainer>
            <CtaContainerContainer $justifyContent={contentAlignment}>
              <CtaContainer $width={ContentWidthPercentages[contentWidthSpan]}>
                <CtaTextWrapper $justifyContent={justifyContent}>
                  <HeadingWrapper $justifyContent={justifyContent}>
                    <CtaTitle>
                      <UnstyledInnerHtml richText={headerText} />
                    </CtaTitle>
                    <CtaText>
                      <InnerHtml richText={ctaDescription} />
                    </CtaText>
                  </HeadingWrapper>
                  {CtaButtons}
                </CtaTextWrapper>
                {Disclaimer}
              </CtaContainer>
              {ctaImageOverlay?.image && (
                <CtaContainer>
                  <img
                    src={autoOptimizeImage(ctaImageOverlay.image?.url)}
                    alt={ctaImageOverlay.altImageText ?? ctaHeader + ': img'}
                    loading='lazy'
                  />
                </CtaContainer>
              )}
            </CtaContainerContainer>
          </ContentContainer>
          <CtaOverlay $radialPosition={radialPosition} />
        </FullBleedStandardBlockCtaContainer>
      </MediaQuery>
    </>
  );
};
export default StandardFullbleedCta;
